import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateInTimeZone } from '../../utils/timezone';

interface CompactDatePickerProps {
  id?: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  min?: string;
  max?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Version compacte du DatePicker avec un calendrier stylisé en popup
 * Idéal pour les espaces restreints comme les drawers
 */
const CompactDatePicker: React.FC<CompactDatePickerProps> = ({
  id,
  name,
  value,
  onChange,
  label,
  min,
  max,
  required = false,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value ? new Date(value) : new Date());
  const containerRef = useRef<HTMLDivElement>(null);

  // Fermer le calendrier en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7; // Lundi = 0

    const days = [];

    // Jours du mois précédent
    for (let i = startingDayOfWeek; i > 0; i--) {
      const prevDate = new Date(year, month, 1 - i);
      days.push({ date: prevDate, isCurrentMonth: false });
    }

    // Jours du mois actuel
    for (let day = 1; day <= daysInMonth; day++) {
      const currentDate = new Date(year, month, day);
      days.push({ date: currentDate, isCurrentMonth: true });
    }

    // Jours du mois suivant pour compléter la grille
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const nextDate = new Date(year, month + 1, day);
      days.push({ date: nextDate, isCurrentMonth: false });
    }

    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (date: Date) => {
    const formattedDate = formatDateInTimeZone(date, 'yyyy-MM-dd');

    // Vérifier si la date est dans la plage autorisée
    if (min && formattedDate < min) return;
    if (max && formattedDate > max) return;

    const syntheticEvent = {
      target: { name, value: formattedDate },
      currentTarget: { name, value: formattedDate }
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
    setIsOpen(false);
  };

  const isDateDisabled = (date: Date) => {
    const formattedDate = formatDateInTimeZone(date, 'yyyy-MM-dd');
    if (min && formattedDate < min) return true;
    if (max && formattedDate > max) return true;
    return false;
  };

  const isSelected = (date: Date) => {
    if (!value) return false;
    const formattedDate = formatDateInTimeZone(date, 'yyyy-MM-dd');
    return formattedDate === value;
  };

  const formatDisplayDate = () => {
    if (!value) return 'Sélectionner une date';
    const date = new Date(value);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const days = getDaysInMonth();
  const monthName = currentMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const dayNames = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

  return (
    <div className={`w-full ${className}`} ref={containerRef}>
      {label && (
        <label
          htmlFor={id || name}
          className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        {/* Input cliquable */}
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`
            w-full pl-10 pr-3 py-2.5 rounded-lg border text-left
            bg-white dark:bg-gray-700
            border-gray-300 dark:border-gray-600
            text-gray-900 dark:text-gray-100
            text-sm
            focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary
            hover:border-gray-400 dark:hover:border-gray-500
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all duration-200
            ${!value ? 'text-gray-400 dark:text-gray-500' : ''}
          `}
        >
          {formatDisplayDate()}
        </button>

        {/* Icône calendrier */}
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </div>

        {/* Calendrier popup */}
        {isOpen && (
          <div className="absolute z-50 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 min-w-[280px]">
            {/* Header navigation */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-medium capitalize">{monthName}</span>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Jours de la semaine */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Grille des jours */}
            <div className="grid grid-cols-7 gap-1">
              {days.map(({ date, isCurrentMonth }, index) => {
                const disabled = isDateDisabled(date);
                const selected = isSelected(date);

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => !disabled && isCurrentMonth && handleDateClick(date)}
                    disabled={disabled || !isCurrentMonth}
                    className={`
                      aspect-square text-xs rounded flex items-center justify-center
                      transition-all duration-150
                      ${!isCurrentMonth
                        ? 'text-gray-300 dark:text-gray-600 cursor-default'
                        : disabled
                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          : selected
                            ? 'bg-primary text-white font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }
                    `}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompactDatePicker;
